import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../api";

interface Job {
  job_id: string;
  title: string;
  company: string;
  location?: string;
  url: string;
  status: string;
  score?: number;
  decision?: string;
  ingested_at?: string;
  fetch_error?: string;
  fit_summary?: string;
  salary_text?: string;
  salary_annual_min?: number;
  salary_annual_max?: number;
  salary_below_min?: boolean;
}

// Sort key for the salary column: top of the posted range, falling back to the
// bottom. Undefined for postings with no salary, which always sort last.
const salaryValue = (j: Job) => j.salary_annual_max ?? j.salary_annual_min;

const STATUSES = [
  "all",
  "new",
  "fetched",
  "scored",
  "prepared",
  "applied",
  "skipped",
  "fetch_failed",
] as const;

type SortKey = "score" | "ingested" | "salary";

const STATUS_BADGE: Record<string, string> = {
  new: "bg-slate-800 text-slate-300",
  fetched: "bg-sky-900/40 text-sky-200",
  scored: "bg-indigo-900/40 text-indigo-200",
  prepared: "bg-emerald-900/40 text-emerald-200",
  applied: "bg-emerald-900/40 text-emerald-200",
  skipped: "bg-slate-800 text-slate-500",
  fetch_failed: "bg-rose-900/40 text-rose-200",
};

export function Inbox() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const [filter, setFilter] = useState<(typeof STATUSES)[number]>("all");
  const [q, setQ] = useState("");
  const [hideScored, setHideScored] = useState(true);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "ingested",
    dir: "desc",
  });

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  const sortArrow = (key: SortKey) =>
    sort.key === key ? (sort.dir === "desc" ? " ↓" : " ↑") : "";

  const list = useQuery({
    queryKey: ["jobs"],
    queryFn: () => api<{ jobs: Job[]; count: number }>("/jobs?limit=500", token),
    enabled: !!token,
    refetchInterval: 8000,
  });

  const jobs = (list.data?.jobs ?? [])
    .filter((j) => filter === "all" || j.status === filter)
    // Hide "scored" jobs (evaluated but below tailor threshold) unless explicitly viewing them.
    .filter((j) => !(hideScored && filter !== "scored" && j.status === "scored"))
    .filter((j) =>
      q.trim() === ""
        ? true
        : `${j.title} ${j.company} ${j.location ?? ""}`.toLowerCase().includes(q.toLowerCase()),
    )
    .sort((a, b) => {
      // Missing values always sort last, regardless of direction.
      const value = (j: Job) =>
        sort.key === "score"
          ? j.score
          : sort.key === "salary"
          ? salaryValue(j)
          : j.ingested_at
          ? Date.parse(j.ingested_at)
          : undefined;
      const av = value(a);
      const bv = value(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sort.dir === "desc" ? bv - av : av - bv;
    });

  return (
    <div className="space-y-5">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-50">Inbox</h1>
          <p className="mt-1 text-sm text-slate-400">
            {list.data?.count ?? 0} job{list.data?.count === 1 ? "" : "s"} ingested. Sorted by{" "}
            {sort.key === "score" ? "fit score" : sort.key === "salary" ? "salary" : "ingested date"} (
            {sort.dir === "desc" ? "high → low" : "low → high"}). Click a column to change.
          </p>
        </div>
        <input
          type="search"
          placeholder="Search title / company / location"
          className="w-72 rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-slate-500"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </header>

      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        {STATUSES.map((s) => {
          const count =
            s === "all"
              ? list.data?.count ?? 0
              : (list.data?.jobs ?? []).filter((j) => j.status === s).length;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`rounded-full border px-3 py-1 transition ${
                filter === s
                  ? "border-accent bg-accent/20 text-accent"
                  : "border-slate-700 text-slate-300 hover:border-slate-500"
              }`}
            >
              {s} ({count})
            </button>
          );
        })}
        <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-slate-400">
          <input
            type="checkbox"
            checked={hideScored}
            onChange={(e) => setHideScored(e.target.checked)}
            className="accent-accent"
          />
          Hide scored
        </label>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
        {list.isPending && <p className="p-6 text-slate-400">Loading…</p>}
        {!list.isPending && jobs.length === 0 && (
          <p className="p-6 text-sm text-slate-500">No jobs match this filter.</p>
        )}
        {jobs.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-950/40 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Company</th>
                <th className="px-4 py-2 text-left">Location</th>
                <th className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => toggleSort("salary")}
                    className="text-xs uppercase tracking-wide hover:text-slate-300"
                  >
                    Salary{sortArrow("salary")}
                  </button>
                </th>
                <th className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => toggleSort("score")}
                    className="text-xs uppercase tracking-wide hover:text-slate-300"
                  >
                    Score{sortArrow("score")}
                  </button>
                </th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">
                  <button
                    type="button"
                    onClick={() => toggleSort("ingested")}
                    className="text-xs uppercase tracking-wide hover:text-slate-300"
                  >
                    Ingested{sortArrow("ingested")}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {jobs.map((j) => (
                <tr key={j.job_id} className="transition hover:bg-slate-800/40">
                  <td className="px-4 py-2">
                    <Link to={`/jobs/${j.job_id}`} className="text-slate-100 hover:text-accent">
                      {j.title}
                    </Link>
                    {j.fit_summary && (
                      <div className="mt-0.5 line-clamp-1 text-xs text-slate-500">{j.fit_summary}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-300">{j.company}</td>
                  <td className="px-4 py-2 text-slate-400">{j.location ?? "—"}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-right">
                    {j.salary_text ? (
                      <span
                        className={j.salary_below_min ? "text-amber-300" : "text-slate-300"}
                        title={j.salary_below_min ? "Below your minimum salary" : undefined}
                      >
                        {j.salary_text}
                        {j.salary_below_min && " ⚠"}
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {j.score != null ? (
                      <span
                        className={`inline-flex h-7 min-w-[2.5rem] items-center justify-center rounded-full px-2 text-xs font-medium ${
                          j.score >= 75
                            ? "bg-emerald-900/40 text-emerald-200"
                            : j.score >= 50
                            ? "bg-amber-900/40 text-amber-200"
                            : "bg-slate-800 text-slate-400"
                        }`}
                      >
                        {j.score}
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs ${
                        STATUS_BADGE[j.status] ?? "bg-slate-800 text-slate-300"
                      }`}
                    >
                      {j.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {j.ingested_at ? new Date(j.ingested_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
